import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'

import { getExchangeByKey } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

interface ExchangeIconProps {
  exchange: string
  className?: string
  fallback?: React.ReactNode
  logoUrl?: string
  brandfetchDomain?: string
  logoObjectFit?: 'cover' | 'contain'
  brandfetchTheme?: 'light' | 'dark'
}

export const ExchangeIcon = ({
  exchange,
  className,
  fallback,
  logoUrl,
  brandfetchDomain,
  logoObjectFit,
  brandfetchTheme,
}: ExchangeIconProps) => {
  const [isError, setError] = useState(false)
  const [triedFallback, setTriedFallback] = useState(false)
  const clientId = process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID
  const { resolvedTheme } = useTheme()

  const normalizedExchange = exchange.toLowerCase()
  const exchangeConfig = getExchangeByKey(normalizedExchange)
  const registryLogoUrl = exchangeConfig?.logoUrl
  const hasRegistryLogoUrl = Boolean(registryLogoUrl)
  const resolvedBrandfetchDomain = brandfetchDomain ?? exchangeConfig?.brandfetchDomain
  const resolvedLogoObjectFit = logoObjectFit ?? exchangeConfig?.logoObjectFit ?? 'cover'

  // Pick the logo variant that has contrast against the app's CURRENT surface
  // color, not just whichever variant the registry happens to have been
  // authored with — a light-colored (brandfetchLightThemePath) wordmark meant
  // for OrderX's dark surfaces goes invisible once the app switches to light
  // theme, and vice versa. Falls back to brandfetchLightThemePath for
  // exchanges that only ever specified one variant, matching prior behavior.
  const registryThemePath =
    resolvedTheme === 'light'
      ? (exchangeConfig?.brandfetchDarkThemePath ?? exchangeConfig?.brandfetchLightThemePath)
      : exchangeConfig?.brandfetchLightThemePath

  const brandfetchPath =
    (brandfetchTheme && resolvedBrandfetchDomain
      ? `${resolvedBrandfetchDomain}/w/800/h/313/theme/${brandfetchTheme}/logo`
      : '') ||
    (registryThemePath ??
      (resolvedBrandfetchDomain ? `${resolvedBrandfetchDomain}/w/400/h/400` : ''))
  const brandfetchLogoUrl = brandfetchPath
    ? `https://cdn.brandfetch.io/${brandfetchPath}?c=${clientId}`
    : ''
  const primaryUrl = brandfetchTheme
    ? brandfetchLogoUrl
    : (logoUrl ?? registryLogoUrl ?? brandfetchLogoUrl)
  const fallbackUrl = primaryUrl

  // Reset state when exchange (or the theme-driven logo variant) changes
  useEffect(() => {
    setError(false)
    setTriedFallback(false)
  }, [exchange, logoUrl, brandfetchDomain, brandfetchTheme, resolvedTheme])

  const handleError = () => {
    if (!triedFallback) {
      setTriedFallback(true)
    } else {
      setError(true)
    }
  }

  const src = triedFallback ? fallbackUrl : primaryUrl

  if (isError || !primaryUrl) {
    return (
      <div className={cn('relative overflow-hidden', className)}>
        {fallback ? (
          fallback
        ) : (
          <div className="bg-foreground/10 text-foreground/60 flex h-full w-full items-center justify-center text-[10px]">
            {exchange.slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn('relative overflow-hidden', hasRegistryLogoUrl && 'p-0.5', className)}>
      <img
        src={src}
        alt={exchange}
        // A logo is never something to drag out: inside a draggable tab/row the browser's
        // native image drag took the press instead of the reorder.
        draggable={false}
        className={cn([
          'h-full w-full',
          resolvedLogoObjectFit === 'contain' ? 'object-contain' : 'object-cover',
        ])}
        onError={handleError}
      />
    </div>
  )
}
